import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Control } from "src/model/class/general classes/control";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { StateControl } from "src/model/enum/enumControl";
import { ClickActionType } from "src/model/enum/enumCustomInterface";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { AddressMethod } from "src/components/shared-components/interfaces/address-method";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AddressService } from "src/service/address.service";
import { AlertService } from "src/service/alert.service";
import { LoadControlService } from "src/service/load-control.service";
import { NetworkService } from "src/service/network.service";
import { PopupService } from "src/service/popup.service";
import { AddressSearchComponent } from "../address-search/address-search.component";

@Component({
    selector: "app-load-control-form",
    templateUrl: "./load-control-form.component.html",
    styleUrls: ["./load-control-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoadControlFormComponent
  implements OnInit, OnDestroy, iTranslation {
  @Input("control") ctrl: Control = null;
  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;
  public controlGroupForm: UntypedFormGroup;

  private _blockUpdate: boolean = false;
  private _addressControl: Address;
  private _onAddress: Address;
  private _offAddress: Address;

  public toolBarColor: string = "primary";

  private _lblRead = "*";
  private _lblWrite = "*";
  private _lblMOp = "*";
  private _lblOp2 = "*";
  private _lblWriteAddress = "*";
  private _lblValueToWrite = "*";
  private _lblRemoveValue = "*";
  private _lblValueAlreadyUsed = "*";

  public value1: string = "";
  public value2: string = "";
  public nameAddress: string = "";
  public nameOnAddress: string = "";
  public nameOffAddress: string = "";

  public disableOnCmd = true;
  public disableOffCmd = true;

  public stateCtrl = StateControl;

  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public maxPriority: number = 100;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _loadControlService: LoadControlService,
    private _modalCtrl: ModalController,
    private _networkService: NetworkService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    private _addressService: AddressService,
    private _popupService: PopupService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    this._networkServiceSubscription.unsubscribe();

    if (this._translateSubscription) this._translateSubscription.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.operation",
        "dictionary.operationOnAddress",
        "dictionary.read",
        "dictionary.write",
        "dictionary.writeAddress",
        "dictionary.valueToWrite",
        "dictionary.removeValueAsk",
        "dictionary.valueUsed",
      ])
      .subscribe((translation) => {
        this._lblMOp = translation["dictionary.operation"];
        this._lblOp2 = translation["dictionary.operationOnAddress"];
        this._lblRead = translation["dictionary.read"];
        this._lblWrite = translation["dictionary.write"];
        this._lblWriteAddress = translation["dictionary.writeAddress"];
        this._lblValueToWrite = translation["dictionary.valueToWrite"];
        this._lblRemoveValue = translation["dictionary.removeValueAsk"];
        this._lblValueAlreadyUsed = translation["dictionary.valueUsed"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  private initializeForm() {
    this.controlGroupForm = this._formBuilder.group(
      {
        name: new UntypedFormControl("", [Validators.required]),
        description: new UntypedFormControl("", [Validators.required]),
        state: new UntypedFormControl(this.stateCtrl.Auto, { updateOn: "change" }),
        stateAddress: new UntypedFormControl(""),
        onAddress: new UntypedFormControl(""),
        onCommand: new UntypedFormControl(""),
        offAddress: new UntypedFormControl(""),
        offCommand: new UntypedFormControl(""),
        priority: new UntypedFormControl(0),
      },
      { updateOn: "blur" }
    );

    
    this.controlGroupForm.get('onAddress').valueChanges
    .subscribe(value => {
      const onCmdControl = this.controlGroupForm.get('onCommand');
      if (!value) {
        onCmdControl.disable();
      } else {
        onCmdControl.enable();
      }
    });

    this.controlGroupForm.get('offAddress').valueChanges
    .subscribe(value => {
      const onCmdControl = this.controlGroupForm.get('offCommand');
      if (!value) {
        onCmdControl.disable();
      } else {
        onCmdControl.enable();
      }
    });

    this.controlGroupForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(AppSettings.DEFAULT_FORM_DEBOUNCE_TIME)
      )
      .subscribe((changedFormValue) => {
        this.onSave();
      });
  }

  /**
   * Called by parent class when the current object is changed
   */
  async ngOnChanges() {
    this._blockUpdate = true;
    if (this.ctrl) {
      if (this.ctrl.idControlGroup != null) {
        const controls =
          await this._loadControlService.GetControlByControlGroupID(
            this.ctrl.idControlGroup
          );
        if (controls instanceof Array)
          this.maxPriority = Math.max.apply(
            Math,
            controls.map(function (c) {
              return c.priority;
            })
          );
        else if (controls instanceof Control)
          this.maxPriority = controls.priority;
      }
      /**
       * with patchValue function i update form field: there aren't expressed any field because
       * there is 1:1 relation between form field and object passed to patchValue.
       * emitEvent is set to false to prevent first emission during onChange event (during initialize form)
       */
      this.controlGroupForm.patchValue(this.ctrl, {
        onlySelf: true,
        emitEvent: false,
      });

      if (
        this.ctrl.stateAddress != "" &&
        this.ctrl.instanceGUIDStateAddress != ""
      ) {
        this.loadAddressInfo(
          1,
          this.ctrl.instanceGUIDStateAddress,
          this.ctrl.stateAddress
        );
      } else {
        this.nameAddress = "";
      }

      if (this.ctrl.onCommand != null && this.ctrl.onCommand != "") {
        let cmds = this.ctrl.onCommand.split(" ");
        let adds = cmds[0].split("_");
        this.loadAddressInfo(2, adds[0], adds[1]);
        this.controlGroupForm.patchValue(
          {
            onAddress: adds[1],
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      } else {
        this.controlGroupForm.patchValue(
          {
            onAddress: "",
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
        this.nameOnAddress = "";
        this.value1 = "";
        this._onAddress = null;
      }
      if (this.ctrl.offCommand != null && this.ctrl.offCommand != "") {
        let cmds = this.ctrl.offCommand.split(" ");
        let adds = cmds[0].split("_");
        this.loadAddressInfo(3, adds[0], adds[1]);
        this.controlGroupForm.patchValue(
          {
            offAddress: adds[1],
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      } else {
        this.controlGroupForm.patchValue(
          {
            offAddress: "",
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
        this.nameOffAddress = "";
        this.value2 = "";
        this._offAddress = null;
      }

      this.disableOnCmd = this.controlGroupForm.get("onAddress").value == "";
      this.disableOffCmd = this.controlGroupForm.get("offAddress").value == "";
    }

    this._blockUpdate = false;
  }

  public async openAddressSearch(value: number) {
    const modal = await this._modalCtrl.create({
      component: AddressSearchComponent,
      cssClass: "modal-bigger",
    });

    modal.present();

    const { data } = await modal.onWillDismiss();

    if (modal) {
      modal.dismiss();
    }

    if (data && data.selectedAddress != "") {
      if (value == 1) {
        this._addressControl = data.selectedAddress;
        this.controlGroupForm.patchValue({
          stateAddress: data.selectedAddress.address,
        });
        this.ctrl.instanceGUIDStateAddress = data.selectedAddress.instanceGUID;
        this.ctrl.stateAddress = data.selectedAddress.address;
        this.nameAddress = data.selectedAddress.fullName;
      } else if (value == 2) {
        this._onAddress = data.selectedAddress;
        this.controlGroupForm.patchValue({
          onAddress: data.selectedAddress.address,
        });
        this.nameOnAddress = data.selectedAddress.fullName;
        this.disableOnCmd = this.controlGroupForm.get("onAddress").value == "";
      } else if (value == 3) {
        this._offAddress = data.selectedAddress;
        this.controlGroupForm.patchValue({
          offAddress: data.selectedAddress.address,
        });
        this.nameOffAddress = data.selectedAddress.fullName;
        this.disableOffCmd =
          this.controlGroupForm.get("offAddress").value == "";
      }
    }
  }

  /**
   * Open the popup to choose the address value on the base of the address kind and the available values
   * @param t
   */
  public async openAddressValue(t: number) {
    const address: Address = t == 1 ? this._onAddress : this._offAddress;
    let commandToWrite: string = await this.openPopupSelectValueAddress(
      address,
      false
    );

    if (commandToWrite !== "" && commandToWrite === ClickActionType.WRITE) {
      const resultOperation = await this._alertService.presentPrompt(
        this._lblWrite,
        this._lblWriteAddress,
        this._lblValueToWrite,
        address.hiLevelDataFormat
      );
      if (resultOperation !== false) {
        commandToWrite = resultOperation.toString();
      } else {
        commandToWrite = "";
      }
    }
    if (commandToWrite !== "") {
      switch (t) {
        case 1:
          this.value1 = StaticClass.GetStateNameByValue(
            this._onAddress.states,
            commandToWrite
          );
          if (!this.isValueUsed(this.value1, t)) {
            this.controlGroupForm.patchValue({
              onCommand: this.value1,
            });
          } else {
            this.openPopupValue();
            this.value1 = this.controlGroupForm.get("onCommand").value;
          }
          break;
        case 2:
          this.value2 = StaticClass.GetStateNameByValue(
            this._offAddress.states,
            commandToWrite
          );
          if (!this.isValueUsed(this.value2, t)) {
            this.controlGroupForm.patchValue({
              offCommand: this.value2,
            });
          } else {
            this.openPopupValue();
            this.value2 = this.controlGroupForm.get("offCommand").value;
          }
          break;
        default:
          break;
      }
    } else {
      const cancelOperation: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this._lblRemoveValue,
          "",
          ""
        );
      if (cancelOperation) {
        switch (t) {
          case 1:
            this.controlGroupForm.patchValue({
              onCommand: "",
            });
            this.value1 = "";
            break;
          case 2:
            this.controlGroupForm.patchValue({
              offCommand: "",
            });
            this.value2 = "";
            break;
          default:
            break;
        }
      }
    }
  }

  /**
   * Opens the popup to say that the value we arree trying to set ha already been used
   */
  private async openPopupValue() {
    await this._alertService.presentAlertOk(this._lblValueAlreadyUsed, "", "");
  }

  /**
   * Returns true if the value has already been used
   * @param value value to check
   * @param t number of the value
   * @returns true if value has not been used, false otherwise
   */
  private isValueUsed(value: string, t: number): boolean {
    let used: boolean = false;
    switch (t) {
      case 1:
        used = value === this.controlGroupForm.get("offCommand").value;
        break;
      case 2:
        used = value === this.controlGroupForm.get("onCommand").value;
        break;
    }
    return used;
  }

  /**
   * Save new control group form calling api
   */
  async onSave() {
    if (!this._blockUpdate && this.ctrl != null) {
      const control: Control = this.controlGroupForm.value;
      control.instanceGUIDStateAddress = this.ctrl.instanceGUIDStateAddress;
      control.idControlGroup = this.ctrl.idControlGroup;
      control.stateAddress = this.ctrl.stateAddress;
      control.onCommand = this.formatCommand(1, control.onCommand);
      control.offCommand = this.formatCommand(2, control.offCommand);
      if (control.priority < 1 || control.priority > this.maxPriority)
        control.priority = this.ctrl.priority;
      if (this.ctrl.id === 0) {
        const newControl: Control =
          await this._loadControlService.InsertControl(control);

        this.ctrl.id = newControl.id;
      } else {
        const updateControrl: Control =
          await this._loadControlService.UpdateControl(this.ctrl.id, control);
      }
      control.id = this.ctrl.id;
      this.updatePriority(control, this.ctrl.priority);
    }
  }

  /**
   * Change the priorities of the other controls.
   * @param control
   * @param oldPriority
   */
  private async updatePriority(
    control: Control,
    oldPriority: number
  ): Promise<void> {
    const ctrls = await this._loadControlService.GetControlByControlGroupID(
      control.idControlGroup
    );
    if (ctrls instanceof Array) {
      let ctrlToUpdate: Control = null;
      let update: Control = null;
      if (
        control.priority == oldPriority + 1 ||
        control.priority == oldPriority - 1
      ) {
        this.updateForPriority(
          ctrls.find(
            (x: Control) => x.priority == control.priority && x.id != control.id
          ),
          oldPriority
        );
      } else {
        let controls: Array<Control> = [];
        if (control.priority > oldPriority) {
          controls = ctrls.filter(
            (x: Control) =>
              x.id != control.id &&
              x.priority <= control.priority &&
              x.priority > oldPriority
          );
          for (let i = 0; i < controls.length; i++) {
            this.updateForPriority(controls[i], controls[i].priority - 1);
          }
        } else if (control.priority < oldPriority) {
          controls = ctrls.filter(
            (x: Control) =>
              x.id != control.id &&
              x.priority >= control.priority &&
              x.priority < oldPriority
          );
          for (let i = 0; i < controls.length; i++) {
            this.updateForPriority(controls[i], controls[i].priority + 1);
          }
        }
      }
    }
  }

  /**
   * Update the control to which the new priority has been assigned.
   * @param control
   * @param priority
   */
  private updateForPriority(control: Control, priority: number): void {
    if (control != null) {
      control.priority = priority;
      let update = this._loadControlService.UpdateControl(control.id, control);
    }
  }

  private formatCommand(value: number, command: string): string {
    if (command == "") command = "0";
    if (value == 1) {
      if (this._onAddress) {
        let cmds = this._onAddress.states.split("|");
        const index = cmds.findIndex((x) => x.includes(command));
        let cmd = cmds[index].split("=");
        return (
          this._onAddress.instanceGUID +
          "_" +
          this._onAddress.address +
          " " +
          cmd[1] +
          " 0"
        );
      }
    } else if (value == 2) {
      if (this._offAddress) {
        let cmds = this._offAddress.states.split("|");
        const index = cmds.findIndex((x) => x.includes(command));
        let cmd = cmds[index].split("=");
        return (
          this._offAddress.instanceGUID +
          "_" +
          this._offAddress.address +
          " " +
          cmd[1] +
          " 0"
        );
      }
    }
    return "";
  }

  private InverseCommand(command: string): string {
    if (command.includes("_")) {
      let cmds = command.split(" ");
      return cmds[1];
    }
    return command;
  }

  public async loadAddressInfo(
    value: number,
    instanceguid: string,
    address: string
  ) {
    if (instanceguid != null) {
      let addressSearchObj: iAddressSearch = {
        Instanceguid: instanceguid,
        Address: address,
        Name: "",
        Language: this._translateService.getCurrentLang(),
      };
      if (value == 1) {
        this._addressControl =
          this._addressService.getAddressByInstanceGUIDAndAddress(
            instanceguid,
            address
          );
        if (!this._addressControl || this._addressControl == null) {
          await this._addressService
            .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
            .then((result: Array<Address>) => {
              this.nameAddress = result[0].fullName;
              this._addressControl = result[0];
            });
        } else {
          this.nameAddress = this._addressControl.fullName;
        }
      } else if (value == 2) {
        this._onAddress =
          this._addressService.getAddressByInstanceGUIDAndAddress(
            instanceguid,
            address
          );
        if (!this._onAddress || this._onAddress == null) {
          await this._addressService
            .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
            .then((result: Array<Address>) => {
              this.nameOnAddress = result[0].fullName;
              this._onAddress = result[0];
            });
        } else {
          this.nameOnAddress = this._onAddress.fullName;
        }
        this.value1 = StaticClass.GetStateNameByValue(
          this._onAddress.states,
          this.InverseCommand(this.ctrl.onCommand)
        );
        this.controlGroupForm.patchValue(
          {
            onCommand: this.value1,
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      } else if (value == 3) {
        this._offAddress =
          this._addressService.getAddressByInstanceGUIDAndAddress(
            instanceguid,
            address
          );
        if (!this._offAddress || this._offAddress == null) {
          await this._addressService
            .getAddressesByInstanceGUIDAndAddressAndName(addressSearchObj)
            .then((result: Array<Address>) => {
              this.nameOffAddress = result[0].fullName;
              this._offAddress = result[0];
            });
        } else {
          this.nameOffAddress = this._offAddress.fullName;
        }
        this.value2 = StaticClass.GetStateNameByValue(
          this._offAddress.states,
          this.InverseCommand(this.ctrl.offCommand)
        );
        this.controlGroupForm.patchValue(
          {
            offCommand: this.value2,
          },
          {
            onlySelf: true,
            emitEvent: false,
          }
        );
      }
    }
  }

  /**
   * Open popup value select and return selected value
   *
   * @returns
   */
  private async openPopupSelectValueAddress(
    selectedAddress: Address,
    actionWrite: boolean
  ): Promise<string> {
    const address = new Address(selectedAddress);
    let listMethods: Array<AddressMethod> = address.SplitMethods(
      address.loadMethodStringByLanguage(this._translateService.getCurrentLang())
    );

    if (actionWrite) {
      listMethods = [
        ...listMethods,
        {
          name: this._lblWrite,
          value: ClickActionType.WRITE,
        },
      ];
    }

    let commandToWrite: string =
      await this._popupService.showModalAddressMethods(
        this._lblMOp,
        this._lblOp2,
        listMethods
      );

    return (commandToWrite ??= "");
  }
}
